import { useEffect, useState } from "react";
import { useGetBusFeeQuery } from "../../store/api/configurationSlice";
import SelectBox from "./SelectBox";
import { capitalizeString } from "../../utils/dateAndTimeConversion";

const FeeSelector = ({
  bus_fee_id,
  change,
  isPrivate,
  studentIndex = 0,
  firstChildTaken = false,
  onChildTypeChange,
  onFeeError,
  darkTheme = false,
}) => {
  const [fees, setFees] = useState([]);
  const [rawFees, setRawFees] = useState([]);
  const [warning, setWarning] = useState({ text: "", type: "" });
  const { data, isLoading, isSuccess } = useGetBusFeeQuery();

  useEffect(() => {
    if (isSuccess && data.data) {
      setRawFees(data.data.configs);
      const fee = data.data.configs.map((f) => ({
        value: f.id,
        label: `${f.country_name} / ${capitalizeString(f.child_type)} Child / ${capitalizeString(f.service_type)} / ${isPrivate ? "$0" : `$${f.fee}`}`,
      }));
      setFees(fee);
    }
  }, [data, isSuccess]);

  // Sync inline warning with current valid selection
  useEffect(() => {
    if (!bus_fee_id || rawFees.length === 0) {
      setWarning({ text: "", type: "" });
      return;
    }
    const selected = rawFees.find(
      (f) => f.id === bus_fee_id || f.id === Number(bus_fee_id),
    );
    if (!selected) { setWarning({ text: "", type: "" }); return; }

    const isFirstChildFee = selected.child_type?.toLowerCase() === "first";
    const isFirstStudent = studentIndex === 0;

    if (isFirstStudent && !isFirstChildFee) {
      setWarning({ text: "This is your first child. Please select a 'First Child' fee.", type: "error" });
      onFeeError?.(true);
    } else if (isFirstChildFee && firstChildTaken) {
      setWarning({ text: "A 'First Child' fee is already registered. Please confirm this is correct.", type: "warn" });
      onFeeError?.(false);
    } else if (!isFirstStudent && isFirstChildFee && !firstChildTaken) {
      setWarning({ text: "You selected a 'First Child' fee but this is not your first child. Please confirm this is correct.", type: "warn" });
      onFeeError?.(false);
    } else {
      setWarning({ text: "", type: "" });
      onFeeError?.(false);
    }
  }, [bus_fee_id, rawFees, studentIndex, firstChildTaken]);

  const handleChange = (name, value) => {
    const selected = rawFees.find(
      (f) => f.id === value || f.id === Number(value),
    );
    if (selected) {
      const isFirstChildFee = selected.child_type?.toLowerCase() === "first";
      const isFirstStudent = studentIndex === 0;

      if (isFirstStudent && !isFirstChildFee) {
        setWarning({ text: "This is your first child. Please select a 'First Child' fee.", type: "error" });
      }

      if (isFirstChildFee && firstChildTaken) {
        setWarning({ text: "A 'First Child' fee is already registered. Please confirm this is correct.", type: "warn" });
      } else if (!isFirstStudent && isFirstChildFee) {
        setWarning({ text: "You selected a 'First Child' fee but this is not your first child. Please confirm this is correct.", type: "warn" });
      } else {
        setWarning({ text: "", type: "" });
      }

      onChildTypeChange?.(selected.child_type?.toLowerCase());
    } else {
      setWarning({ text: "", type: "" });
      onChildTypeChange?.(null);
    }
    change(name, value);
  };

  const errorStyle = darkTheme
    ? { backgroundColor: "rgba(220,53,69,0.3)", border: "1px solid rgba(255,80,80,0.7)", color: "#ff8080" }
    : { backgroundColor: "rgba(220,53,69,0.1)", border: "1px solid rgba(220,53,69,0.4)", color: "#842029" };

  const warnStyle = darkTheme
    ? { backgroundColor: "rgba(255,193,7,0.2)", border: "1px solid rgba(255,193,7,0.7)", color: "#ffd966" }
    : { backgroundColor: "rgba(255,193,7,0.15)", border: "1px solid #ffc107", color: "#856404" };

  return (
    <>
      <SelectBox
        isLoading={isLoading}
        options={fees}
        name="bus_fee_id"
        val={bus_fee_id}
        change={handleChange}
      />
      {warning.text && (
        <div
          className="mt-1 px-3 py-2 rounded small"
          style={warning.type === "error" ? errorStyle : warnStyle}
        >
          {warning.type === "error" ? "🚫" : "⚠️"} {warning.text}
        </div>
      )}
    </>
  );
};

export default FeeSelector;
